/* Лист «Сортировка и фильтры» по кнопке ⇅ (владелец 30.10.2026): все пункты прежнего меню ⇅ — строками-карточками по группам, как лист
   «Ещё настройки» (.sc-list и .sc-r, legacy/bot-core.css: значок, название, подпись, кружок с галочкой справа). Группа порядка («Сначала») — выбор одного, формат и состав — сразу несколько (Двое + Рилс + Кружки + Новые);
   пустой выбор в такой группе — «все». Выбор применяется сразу, лист не закрывается; «Сбросить» возвращает умолчания всех групп.
   Разметка одна: React-лист студии (FilterSheet.tsx) и лист BotCore для разделов на движках (openFilterSheet ниже) */
import './filter-sheet.css';

export interface FilterItem {
  id: string;
  label: string;
  /** подпись под названием (серым) */
  sub?: string;
  /** значок слева — разметка svg (кружок 40 px, как у «Формата» в «Ещё настройки») */
  icon?: string;
  /** бейдж версии после названия (капсула с номером модели, брендбук: «STEOS Voice ²»; стиль .vbadge) */
  badge?: string;
  /** готовая разметка названия вместо label и badge (лок-ап модели по брендбуку); label остаётся подписью для чтения с экрана */
  lockup?: string;
  disabled?: boolean;
}
export interface FilterGroup {
  key: string;
  label: string;
  /** можно выбрать несколько; пустой выбор — «все» */
  multi?: boolean;
  items: FilterItem[];
  value: string[];
  /** выбор по умолчанию (к нему ведёт «Сбросить»): у порядка — свой, у множественной группы — пусто */
  def?: string[];
}
export interface FilterTexts {
  title: string;
  close: string;
  reset: string;
  done: string;
}

/** Подписи строк по коду пункта (по-русски; переводит раздел, который открывает лист) */
export const FILTER_SUB: Record<string, string> = {
  pop: 'Чаще всего выбирают',
  new: 'Сначала свежие',
  old: 'Сначала давние',
  cheap: 'Сначала недорогие',
  short: 'Сначала короткие ролики',
  name: 'По названию',
  name_desc: 'По названию, с конца алфавита',
  reels: 'Вертикальные ролики',
  photo: 'Картинки и посты',
  video: 'Горизонтальные ролики',
  circle: 'Видеосообщения Telegram',
  one: 'Один человек в кадре',
  two: 'Двое человек в кадре',
  many: 'Трое и больше',
  kid: 'С ребёнком',
  pet: 'С питомцем',
};
const shape = (d: string) => `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">${d}</svg>`;
/** Значки форматов — фигуры кадра, как у «Формата» в «Ещё настройки» */
export const FILTER_ICON: Record<string, string> = {
  circle: shape('<circle cx="12" cy="12" r="8"/>'),
  reels: shape('<rect x="8" y="3.5" width="8" height="17" rx="2.5"/>'),
  video: shape('<rect x="3.5" y="8" width="17" height="8" rx="2.5"/>'),
  photo: shape('<rect x="5" y="5" width="14" height="14" rx="2.5"/>'),
};

const esc = (s: string) => s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);
const same = (a: string[], b: string[]) => a.length === b.length && a.every(x => b.includes(x));
const defOf = (g: FilterGroup) => g.def ?? (g.multi ? [] : g.value);

/** Выбор отличается от умолчаний — у кнопки ⇅ заливка, у «Сбросить» — доступность */
export const filtersOn = (groups: FilterGroup[]) => groups.some(g => !same(g.value, defOf(g)));

/** Новый выбор группы после нажатия плитки id: одиночная — только она, множественная — переключение */
export function toggle(g: FilterGroup, id: string): string[] {
  if (!g.multi) return [id];
  return g.value.includes(id) ? g.value.filter(x => x !== id) : [...g.value, id];
}

/** Умолчания всех групп, которые от них отличаются */
export const resets = (groups: FilterGroup[]) => groups.filter(g => !same(g.value, defOf(g))).map(g => ({ key: g.key, value: defOf(g) }));

const CHECK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12 4 4L19 6"/></svg>';
const X = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';

/** Тело листа без обёртки .cbody: шапка, группы строк, «Сбросить» и «Готово» */
export function filterSheetInner(groups: FilterGroup[], t: FilterTexts): string {
  const body = groups
    .filter(g => g.items.length)
    .map(
      g => `<div class="glbl fs-gl"><b>${esc(g.label)}</b></div><div class="sc-list fs-list" role="${g.multi ? 'group' : 'radiogroup'}" aria-label="${esc(g.label)}">${g.items
        .map(i => {
          const on = g.value.includes(i.id),
            off = !!i.disabled && !on;
          return `<button class="sc-r fs-r${on ? ' on' : ''}${off ? ' off' : ''}" type="button" role="${g.multi ? 'checkbox' : 'radio'}" aria-checked="${on}" data-fs="${esc(g.key)}" data-fs-id="${esc(i.id)}"${off ? ' disabled' : ''}>${i.icon ? `<span class="sc-th o">${i.icon}</span>` : ''}<span class="m">${i.lockup ? `<b class="fs-lu" aria-label="${esc(i.label)}">${i.lockup}</b>` : `<b>${esc(i.label)}${i.badge ? `<sup class="vbadge">${esc(i.badge)}</sup>` : ''}</b>`}${i.sub ? `<small>${esc(i.sub)}</small>` : ''}</span><span class="rd">${CHECK}</span></button>`;
        })
        .join('')}</div>`,
    )
    .join('');
  return `<div class="shead"><div class="m"><h2>${esc(t.title)}</h2></div><button class="iconb xbtn" type="button" data-bsheet-x aria-label="${esc(t.close)}">${X}</button></div>${body}<div class="cta fs-cta"><button class="btn line" type="button" data-fs-reset${filtersOn(groups) ? '' : ' disabled'}>${esc(t.reset)}</button><button class="btn" type="button" data-bsheet-x>${esc(t.done)}</button></div>`;
}

/** Нажатие внутри листа: плитка или «Сбросить» → изменения групп (пусто — нажатие мимо) */
export function filterClick(target: EventTarget | null, groups: FilterGroup[]): Array<{ key: string; value: string[] }> {
  const el = target instanceof Element ? target.closest<HTMLElement>('[data-fs],[data-fs-reset]') : null;
  if (!el || (el as HTMLButtonElement).disabled) return [];
  if (el.dataset.fsReset !== undefined) return resets(groups);
  const g = groups.find(x => x.key === el.dataset.fs);
  const id = el.dataset.fsId;
  if (!g || id === undefined) return [];
  const value = toggle(g, id);
  return same(value, g.value) ? [] : [{ key: g.key, value }];
}

/* ── Лист для разделов на движках (BotCore.sheet, как BC.choiceSheet): один на приложение, вставляется в #bot при первом открытии ── */
interface LegacySheet {
  html(): string;
  open(html: string): void;
  paint(html: string): void;
  close(): void;
  readonly isOpen: boolean;
  el(): HTMLElement | null;
}
type Opened = { groups: () => FilterGroup[]; t: FilterTexts; onChange(key: string, value: string[]): void };
let sheet: LegacySheet | null = null;
let cur: Opened | null = null;

/** Открыть лист: groups — текущий выбор (зовётся заново после каждого изменения), onChange — применить выбор группы */
export function openFilterSheet(o: Opened) {
  const BC = (window as unknown as { BotCore?: { sheet(o: { id: string; label: string; layer?: boolean; onClose(): void }): LegacySheet } }).BotCore;
  if (!BC) return;
  if (!sheet) {
    /* слоем: ⇅ бывает и внутри листа (выбор голоса) — лист фильтров встаёт поверх, не закрывая его */
    sheet = BC.sheet({ id: 'bcFilters', label: o.t.title, layer: true, onClose: () => (cur = null) });
    document.addEventListener('click', e => {
      const p = sheet?.el();
      if (!cur || !p || !(e.target instanceof Node) || !p.contains(e.target)) return;
      const c = cur;
      const changes = filterClick(e.target, c.groups());
      if (!changes.length) return;
      changes.forEach(x => c.onChange(x.key, x.value));
      if (cur === c && sheet?.isOpen) sheet.paint(`<div class="cbody fs-sh">${filterSheetInner(c.groups(), c.t)}</div>`);
    });
  }
  if (!document.getElementById('bcFilters')) (document.getElementById('bot') || document.body).insertAdjacentHTML('beforeend', sheet.html());
  cur = o;
  sheet.open(`<div class="cbody fs-sh">${filterSheetInner(o.groups(), o.t)}</div>`);
}
